import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import { ViewFeeOrExpenseDetails } from "../../../../../../../../api/services/program/models";
import {
  useCreateProgramExpenseMutation,
  useEditExpenseDetails,
} from "../../../../../../../../api/services/program/programApis";
import {
  ITabs,
  MuiBox,
  MuiModal,
  MuiRadioGroup,
  MuiTabPanel,
  MuiTabs,
  MuiTypography,
} from "../../../../../../../../components";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { KeyValue } from "../../../../../../../../helpers/types/common";
import { replaceHashWithValue } from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  getAddFeesOrExpensePayload,
  setFormExpenseData,
} from "../../../../../../helper";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  AddOrEditExpenseOrFeeFormKey,
  addExpenseFormInitialValue,
} from "../../../../../../modelsAndSchemas/Models";
import { AddOrEditExpenseOrFeeSchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { addFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { AddFeesAndExpenseTabWrapper } from "../../FeesAndExpenseMain.style";
import { AddFeesAndExpenseGeneralForm } from "./AddFeesAndExpenseGeneralForm";
import { AddFeesAndExpenseSummary } from "./AddFeesAndExpenseSummary";
import { AddFeesAndExpenseTaxForm } from "./AddFeesAndExpenseTaxForm";

export interface AddFeesAndExpenseProps {
  showAddFeesAndExpenseModal: boolean;
  onHideAddFeesAndExpenseModal: (isReloadList?: boolean) => void;
  billingCurrencyId: string;
  billingCurrencyName: string;
  currencyList: IdNameList[];
  programId: string;
  selectedExpenseDetails?: ViewFeeOrExpenseDetails | null;
}

const TAB_INDEX = {
  GENERAL: 0,
  TAX: 1,
  SUMMARY: 2,
};

export const AddFeesAndExpense = ({
  onHideAddFeesAndExpenseModal,
  showAddFeesAndExpenseModal,
  programId,
  billingCurrencyId,
  billingCurrencyName,
  selectedExpenseDetails,
  currencyList,
}: AddFeesAndExpenseProps) => {
  const {
    useFetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [selectedTab, setSelectedTab] = useState(TAB_INDEX.GENERAL);
  const [state, setState] = useState<{
    showCancelModal: boolean;
    showTypeChangeModal: boolean;
    type: string;
  }>({
    showCancelModal: false,
    showTypeChangeModal: false,
    type:
      selectedExpenseDetails?.type ??
      ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE,
  });
  const dispatch = useDispatch();
  const {
    mutateAsync: createProgramExpenseMutation,
    isPending: isCreatingProgramExpense,
    isSuccess: isCreateExpenseSuccess,
    isError: isCreateExpenseError,
  } = useCreateProgramExpenseMutation();

  const {
    mutateAsync: editProgramExpenseMutation,
    isPending: isEditingProgramExpense,
    isSuccess: isEditExpenseSuccess,
    isError: isEditExpenseError,
  } = useEditExpenseDetails();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  useEffect(() => {
    const isApiLoading = isCreatingProgramExpense || isEditingProgramExpense;
    if (isApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isCreatingProgramExpense, isEditingProgramExpense]);

  useEffect(() => {
    if (selectedExpenseDetails) {
      setFormExpenseData(
        selectedExpenseDetails,
        setValue,
        billingCurrencyName,
        userCountryDateFormat,
      );
    }
  }, [selectedExpenseDetails]);

  useEffect(() => {
    const isCreateOrEditApiSuccess =
      isCreateExpenseSuccess || isEditExpenseSuccess;
    if (isCreateOrEditApiSuccess) {
      onHideAddFeesAndExpenseModal(true);
    }
  }, [isCreateExpenseSuccess, isEditExpenseSuccess]);

  useEffect(() => {
    const isApiError = isCreateExpenseError || isEditExpenseError;
    if (isApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isCreateExpenseError, isEditExpenseError]);

  const getLabel = useFetchLabel(
    addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );

  const tabIdGeneral = `general-tab`;
  const tabIdTax = `tax-tab`;
  const tabIdSummary = `summary-tab`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.generalTab),
    },
    {
      id: tabIdTax,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.taxTab),
    },
    {
      id: tabIdSummary,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.summaryTab),
    },
  ];

  const formMethod = useForm<KeyValue>({
    mode: "all",
    defaultValues: addExpenseFormInitialValue,
    resolver: yupResolver(
      AddOrEditExpenseOrFeeSchema(commonValidationMessages),
    ),
  });

  const {
    getValues,
    trigger,
    setValue,
    reset,
    formState: { dirtyFields },
    setError,
    clearErrors,
  } = formMethod;

  const isFormDirty = Object.keys(dirtyFields).length > 0;

  const prepopulatedDefaultField = () => {
    setValue(AddOrEditExpenseOrFeeFormKey.billingCurrency, billingCurrencyId);
    setValue(
      AddOrEditExpenseOrFeeFormKey.billingCurrencyName,
      billingCurrencyName,
    );
    if (!selectedExpenseDetails) {
      setValue(
        AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId,
        billingCurrencyId,
      );
      setValue(
        AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyName,
        billingCurrencyName,
      );
    }
  };

  useEffect(() => {
    prepopulatedDefaultField();
  }, [billingCurrencyName]);

  const radioOptions = [
    {
      value: ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE,
      label: getLabel(
        addFeesAndExpensesSiteCoreKeys.programProjectFeeRadioButton,
      ),
    },
    {
      value: ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.expenseRadioButton),
    },
  ];

  const resetFields = () => {
    reset();
    prepopulatedDefaultField();
  };

  const programProjectFeeRadioButtonLabel = getLabel(
    addFeesAndExpensesSiteCoreKeys.programProjectFeeRadioButton,
  );

  const expenseRadioButtonLabel = getLabel(
    addFeesAndExpensesSiteCoreKeys.expenseRadioButton,
  );

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFormDirty) {
      setState((prev) => ({
        ...prev,
        showTypeChangeModal: true,
      }));
      return;
    }
    const type = e.target.value;
    resetFields();
    setState((prev) => ({
      ...prev,
      type,
    }));
    setValue(AddOrEditExpenseOrFeeFormKey.type, type);
  };

  const validateGeneralForm = async () => {
    const isValid = (await trigger()) && checkIsConvertButtonClicked();
    if (isValid) {
      setSelectedTab(TAB_INDEX.TAX);
    }
  };
  const onAddFeesOrExpense = () => {
    const formData = getValues();
    const payload = getAddFeesOrExpensePayload(
      formData,
      programId,
      userCountryDateFormat,
    );
    if (selectedExpenseDetails)
      editProgramExpenseMutation({
        params: payload,
        expenseId: selectedExpenseDetails.expenseDetails.id,
      });
    else
      createProgramExpenseMutation({
        params: payload,
        programId,
      });
  };

  const checkIsConvertButtonClicked = () => {
    const isRateConverted = getValues(
      AddOrEditExpenseOrFeeFormKey.isRateConverted,
    );
    const billingCurrencyId = getValues(
      AddOrEditExpenseOrFeeFormKey.billingCurrency,
    );
    const currencyId = getValues(
      AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId,
    );
    const checkIsExpenseOrFeeCurrencyMismatch =
      currencyId && currencyId !== billingCurrencyId;

    if (!isRateConverted && checkIsExpenseOrFeeCurrencyMismatch) {
      setError(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate, {
        type: "custom",
        message: getLabel(addFeesAndExpensesSiteCoreKeys.exchangeRateError),
      });

      return false;
    } else {
      clearErrors(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate);
    }
    return true;
  };

  const handleTabSubmit = async () => {
    if (selectedTab === TAB_INDEX.GENERAL) {
      validateGeneralForm();
    } else if (selectedTab === TAB_INDEX.TAX) {
      setSelectedTab(TAB_INDEX.SUMMARY);
    } else if (selectedTab === TAB_INDEX.SUMMARY) {
      onAddFeesOrExpense();
    }
  };

  const onTabChange = (_event: React.ChangeEvent<{}>, value: number) => {
    if (selectedTab === TAB_INDEX.GENERAL) {
      validateGeneralForm();
    } else {
      setSelectedTab(value);
    }
  };

  const handleHideAddFeesAndExpenseModal = () => {
    if (isFormDirty) {
      setState((prev) => ({
        ...prev,
        showCancelModal: true,
      }));
    } else {
      onHideAddFeesAndExpenseModal();
    }
  };

  const closeModalHandler = () => {
    setState((prev) => ({
      ...prev,
      showCancelModal: false,
      showTypeChangeModal: false,
    }));
  };

  const modalTitle = () => {
    if (selectedExpenseDetails) {
      return replaceHashWithValue(
        getLabel(addFeesAndExpensesSiteCoreKeys.editHeading),
        {
          FEEOREXPENSENAME:
            selectedExpenseDetails?.expenseDetails.feeName ?? "",
          TYPE:
            selectedExpenseDetails?.type ===
            ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE
              ? expenseRadioButtonLabel
              : getLabel(
                  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE,
                ),
        },
      );
    }
    return (
      fetchComponentTitle(addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses) ??
      ""
    );
  };

  const handleBackButtonClick = () => {
    setSelectedTab((prev) => prev - 1);
  };

  const handleCancelConfirm = () => {
    if (state.showCancelModal) {
      onHideAddFeesAndExpenseModal();
    } else if (state.showTypeChangeModal) {
      resetFields();

      const newType =
        state.type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE
          ? ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE
          : ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE;
      setValue(AddOrEditExpenseOrFeeFormKey.type, newType);

      setState((prev) => ({
        ...prev,
        showTypeChangeModal: false,
        type: newType,
      }));
    }
  };
  const primaryButtonLabel = () => {
    if (selectedTab === TAB_INDEX.SUMMARY) {
      return selectedExpenseDetails ? commonLabels.update : commonLabels.save;
    }
    return commonLabels.next;
  };

  return (
    <>
      <MuiModal
        open={showAddFeesAndExpenseModal}
        onPrimaryButtonClick={handleTabSubmit}
        onClose={handleHideAddFeesAndExpenseModal}
        primaryButtonTitle={primaryButtonLabel()}
        title={modalTitle()}
        secondaryButtonTitle={commonLabels.cancel}
        hasLargeContent
        actionButtonTitle={
          selectedTab !== TAB_INDEX.GENERAL ? commonLabels.back : undefined
        }
        onActionButtonClick={() => handleBackButtonClick()}
        onSecondaryButtonClick={handleHideAddFeesAndExpenseModal}
        className="add-fees-and-expense-modal"
      >
        <AddFeesAndExpenseTabWrapper>
          <MuiBox
            className={classNames({
              "summary-header":
                selectedTab === TAB_INDEX.SUMMARY && !selectedExpenseDetails,
              "radio-container":
                selectedTab !== TAB_INDEX.SUMMARY && !selectedExpenseDetails,
            })}
          >
            {!selectedExpenseDetails &&
              (selectedTab === TAB_INDEX.GENERAL ? (
                <MuiRadioGroup
                  label={getLabel(
                    addFeesAndExpensesSiteCoreKeys.selectTypeHeading,
                  )}
                  wrapperClassName="fee-selection-type-radio-group"
                  value={state.type}
                  onChange={handleTypeChange}
                  options={radioOptions}
                  requiredARIALabel={commonARIALabel.required}
                />
              ) : (
                <MuiTypography variant="h3Bold">
                  {getLabel(addFeesAndExpensesSiteCoreKeys.typeHeading)}
                  {state.type ===
                  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE
                    ? programProjectFeeRadioButtonLabel
                    : expenseRadioButtonLabel}
                </MuiTypography>
              ))}
          </MuiBox>
          <MuiTabs tabs={tabs} value={selectedTab} onChange={onTabChange}>
            <FormProvider {...formMethod}>
              <MuiTabPanel
                panelId={`panel1`}
                isPanelWithMount={true}
                value={selectedTab}
                index={0}
                ariaLabelledById={tabIdGeneral}
                data-testid="tab-panel-general"
              >
                <AddFeesAndExpenseGeneralForm
                  currencyList={currencyList}
                  type={state.type}
                  transactionDateValue={
                    selectedExpenseDetails?.expenseDetails?.transactionDate
                  }
                  feeCurrencyValue={
                    selectedExpenseDetails?.expenseDetails?.feeCurrency?.id
                  }
                />
              </MuiTabPanel>
              <MuiTabPanel
                panelId={`panel2`}
                isPanelWithMount={true}
                value={selectedTab}
                index={1}
                ariaLabelledById={tabIdTax}
                data-testid="tab-panel-tax"
              >
                <AddFeesAndExpenseTaxForm />
              </MuiTabPanel>
              <MuiTabPanel
                panelId={`panel3`}
                value={selectedTab}
                index={2}
                ariaLabelledById={tabIdSummary}
                data-testid="tab-panel-summary"
              >
                <AddFeesAndExpenseSummary type={state.type} />
              </MuiTabPanel>
            </FormProvider>
          </MuiTabs>
        </AddFeesAndExpenseTabWrapper>
      </MuiModal>
      <CancelFieldModal
        title={
          state.showTypeChangeModal ? commonLabels.confirmation : undefined
        }
        showModal={state.showCancelModal || state.showTypeChangeModal}
        cancelMessage={getLabel(
          state.showTypeChangeModal
            ? addFeesAndExpensesSiteCoreKeys.typeChangeConfirmation
            : addFeesAndExpensesSiteCoreKeys.cancelConfirmMessage,
        )}
        handleModalClose={closeModalHandler}
        handlePrimaryButtonClick={handleCancelConfirm}
      />
    </>
  );
};
